import { useCallback } from "react";

type UseSectionNameWithIndexOptions = {
  /**
   * Generic prefix used when we don't have a baseName (or when baseName itself is a generic "Section N").
   * Defaults to "Section".
   */
  genericPrefix?: string;
};

type GetSectionNameWithIndexArgs = {
  /** Typically product name. */
  baseName?: string | null;
  /** Current number of sections. Next index is existingCount + 1. */
  existingCount?: number | null;
};

const escapeRegex = (input: string): string => input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Builds a section name like "<Product Name> 2" (matching Packaging & Labelling behavior),
 * with safe defaults and protection against "Section 1 2" style names.
 */
export function useSectionNameWithIndex(opts?: UseSectionNameWithIndexOptions) {
  const genericPrefix = String(opts?.genericPrefix ?? "Section").trim() || "Section";

  return useCallback(
    ({ baseName, existingCount }: GetSectionNameWithIndexArgs): string => {
      const safeExistingCount =
        typeof existingCount === "number" && Number.isFinite(existingCount) && existingCount >= 0
          ? existingCount
          : 0;
      const nextIndex = safeExistingCount + 1;

      const rawBase = String(baseName ?? "").trim();
      const resolvedBase = rawBase;

      if (!resolvedBase) return `${genericPrefix} ${nextIndex}`;

      // If the base is already a generic "Section N" (or configured prefix), replace the number.
      // This prevents names like "Section 1 2" when callers pass "Section 1" as the base.
      const genericRe = new RegExp(`^${escapeRegex(genericPrefix)}\\s+\\d+$`, "i");
      if (genericRe.test(resolvedBase)) {
        return `${genericPrefix} ${nextIndex}`;
      }

      return `${resolvedBase} ${nextIndex}`;
    },
    [genericPrefix]
  );
}


